import { useConfirmation } from '@/hooks/useConfirmation';
import { CompilerWorkspace } from '@/components/compiler/CompilerWorkspace';
import { isWorkspaceTab, parseWorkspacePins, serializeWorkspacePins, DEFAULT_WORKSPACE_PINS, type WorkspaceTabKey } from '@/components/compiler/workspaceTabs';
import { DEFAULT_VIEW_PRESET, parseViewPreset, presetDefaultTreeRatio, type WorkspaceViewPreset } from '@/components/compiler/workspaceLayout';
import { navigationApplicableDocs } from '@/lib/compiler/navigationApplicability';
import { useNavigationCapability } from '@/components/compiler/inspector/navigation/useNavigationCapability';
import { SubmissionWorkspaceLoading } from '@/components/SubmissionWorkspaceLoading';
import { SubmissionDeleteButton } from '@/components/submission/SubmissionDeleteButton';
import { useSubmissionViewState, useSubmissionWorkspace } from '@/hooks/useSubmissionWorkspace';
import { SubmissionWorkspaceTabs } from '@/components/SubmissionWorkspaceTabs';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  v2,
  type Submission,
  type Document,
  type SubmissionValidationResultsResponse,
  ApiError,
} from '@/api/v2Client';
import { compilerApi, type SectionNode, type SectionTreeResponse } from '@/lib/api/compiler';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { SubmissionIdentityHeader, type SubmissionLike } from '@/components/SubmissionHeroCard';
import { EditSubmissionModal } from '@/components/compiler/EditSubmissionModal';
import { ApplicationSectionTree } from '@/components/compiler/ApplicationSectionTree';
import { listAllSubmissionDocuments } from '@/lib/listAllSubmissionDocuments';
import { DocumentList } from '@/components/compiler/DocumentList';
import { type SectionOption } from '@/components/compiler/inspector/PlacementTab';
import { PackageTab } from '@/components/compiler/inspector/PackageTab';
import { LifecycleWorkspacePanel } from '@/components/compiler/LifecycleWorkspacePanel';
import { NavigationTab } from '@/components/compiler/inspector/NavigationTab';
import { NavigationValidationPanel } from '@/components/compiler/inspector/navigation/NavigationValidationPanel';
import { NavigationSnapshotControls } from '@/components/compiler/inspector/navigation/NavigationSnapshotControls';
import { NavigationReleaseReview } from '@/components/compiler/inspector/navigation/NavigationReleaseReview';
import { WorkspaceReviewPanel } from '@/components/compiler/WorkspaceReviewPanel';
import { SelectedFileHeader, WorkspaceEmptyState } from '@/components/compiler/workspacePanels';
import { DocumentDetailSections } from '@/components/compiler/DocumentDetailSections';
import { swapAdjacent } from '@/components/compiler/reorderDocs';
import { orderDroppedDocuments, readDropTarget, uploadDroppedFiles, type DocumentDropPosition } from '@/components/compiler/documentDrop';
import { ValidationStatusBanner } from '@/components/compiler/ValidationStatusBanner';
import { CompilerToolbar } from '@/components/compiler/CompilerToolbar';
import { CompilerSpecificationPanel } from '@/components/compiler/CompilerSpecificationPanel';
import { CompileSubmissionPanel } from '@/components/compiler/CompileSubmissionPanel';
import { PublishWizard } from '@/components/compiler/PublishWizard';
import { SubmissionLinksPanel } from '@/components/compiler/SubmissionLinksPanel';
import { ClassificationTabPanel } from '@/components/compiler/ClassificationTabPanel';
import { ValidationReportModal } from '@/components/compiler/ValidationReportModal';
import { AgencyConfirmModal } from '@/components/compiler/AgencyConfirmModal';
import { UnlockSequenceButton } from '@/components/compiler/UnlockSequenceButton';
import { Button } from '@/components/shared/Button';
import { SequenceWarningModal } from '@/components/compiler/SequenceWarningModal';
import { useStatusEvidence } from '@/hooks/useStatusEvidence';
import { subscribeSubmissionRefresh } from '@/lib/submissionRefresh';
import { EvidenceStatusPipeline } from '@/components/submission/EvidenceStatusPipeline';
import { isSequenceLocked, LOCKED_EDIT_TOOLTIP } from '@/constants/status';
import { CompilerNotificationDrawer } from '@/components/compiler/CompilerNotificationDrawer';
import { ReclassifyConfirmDialog } from '@/components/compiler/ReclassifyConfirmDialog';
import { PageToast } from '@/components/shared/PageToast';
import { useUiToast } from '@/hooks/useUiToast';
import { useSetting } from '@/hooks/useSetting';
import { useUndoStack } from '@/hooks/useUndoStack';
import { type Region } from '@/constants/regions';
import { sectionKey } from '@/lib/sectionKey';
import { PackageViewer } from '@/components/PackageViewer';

/**
 * Compiler / Review page with real submission data.
 *
 * phase-compiler-tab-pins — the page builds the six task panels (Documents,
 * Review, Bookmarks & Links, Validation, Lifecycle, Package) plus the CTD tree
 * and the one shared selected-file header, then hands them to CompilerWorkspace,
 * which owns the seven pinnable tabs (the CTD tree is now a first-class pinnable
 * tab of its own, split across two pane-attached headers). The old three-pane
 * layout (left SectionTree / middle DocumentList / right DocumentInspector) and
 * its nested middle- and inspector-tab strips are gone.
 *
 * The header reuses the shared SubmissionIdentityHeader so the Compiler page
 * matches the Dashboard / Documents pages (DrugName + Region + Status etc.).
 */

const wrapperStyle: CSSProperties = {
  // AppShell supplies the same page inset as Submission Details.
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  // A MINIMUM (not a fixed height), so the compiler fills at least the viewport but
  // the outer page may grow taller when the panes want more room — the panes are
  // sized for comfortable reading and each still scrolls internally for genuinely
  // long content, so the page never grows unbounded.
  minHeight: 'calc(100vh - 80px)',
  gap: 12,
};

const pickSubmissionStyle: CSSProperties = {
  marginTop: 12,
  padding: '12px 16px',
  background: 'var(--surface2)',
  border: '1px dashed var(--border)',
  borderRadius: 6,
  color: 'var(--text-muted)',
  fontSize: 13,
};

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function initialAsync<T>(): AsyncState<T> {
  return { data: null, loading: false, error: null };
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const body = JSON.parse(err.body) as { message?: string; error?: string };
      return body.message ?? body.error ?? `HTTP ${err.status}`;
    } catch {
      return err.message;
    }
  }
  if (err instanceof Error) return err.message;
  return String(err);
}


// ── phase-ui-compiler-tree-documents-pane (Sharon, 2026-07-17) ───────────────
// Default documents view: with no section selected, the middle pane shows ALL
// of the sequence's documents grouped by placed CTD section (group headers in
// tree order), with unplaced/unclassified documents in an "Unplaced" group on
// top. Clicking a tree section filters as before; "Show all sections" returns
// to this grouped view.
interface DocGroup {
  /** Tree-form section code ('m1.2') or null for the Unplaced group. */
  readonly code: string | null;
  /** Header label: section label from the tree, or the raw stored code. */
  readonly label: string;
  readonly docs: readonly Document[];
}

function buildDocGroups(
  tree: readonly { code: string; label: string; children?: readonly unknown[] }[] | undefined,
  docs: readonly Document[],
): DocGroup[] {
  // DFS the tree once to get tree order + labels keyed by sectionKey.
  const order = new Map<string, { code: string; label: string; idx: number }>();
  let i = 0;
  const walk = (nodes: readonly { code: string; label: string; children?: readonly unknown[] }[]): void => {
    for (const n of nodes) {
      order.set(sectionKey(n.code), { code: n.code, label: n.label, idx: i++ });
      if (n.children && n.children.length > 0) {
        walk(n.children as readonly { code: string; label: string; children?: readonly unknown[] }[]);
      }
    }
  };
  if (tree) walk(tree);

  const unplaced: Document[] = [];
  const byKey = new Map<string, Document[]>();
  for (const d of docs) {
    const key = sectionKey(d.CtdSectionCode ?? d.Classification ?? '');
    if (key === '') {
      unplaced.push(d);
    } else {
      const list = byKey.get(key) ?? [];
      list.push(d);
      byKey.set(key, list);
    }
  }

  const known: Array<{ idx: number; g: DocGroup }> = [];
  const unknown: DocGroup[] = [];
  for (const [key, list] of byKey) {
    const node = order.get(key);
    if (node) {
      known.push({ idx: node.idx, g: { code: node.code, label: `${node.code} — ${node.label}`, docs: list } });
    } else {
      // Placed under a code the tree doesn't model (e.g. a deep leaf like
      // 3.2.P.5.1) — still shown, labelled by its stored code, after the
      // tree-ordered groups.
      unknown.push({ code: key, label: key, docs: list });
    }
  }
  known.sort((a, b) => a.idx - b.idx);
  unknown.sort((a, b) => (a.label < b.label ? -1 : 1));

  const groups: DocGroup[] = [];
  if (unplaced.length > 0) groups.push({ code: null, label: 'Unplaced — not yet classified', docs: unplaced });
  groups.push(...known.map((k) => k.g), ...unknown);
  return groups;
}

// Flatten the section tree into a single-level list of dropdown options
// (depth-first). The label is the node's own label — parent context comes from
// the code itself (e.g. "3.2.S.4.1"). Used by the selected-document Placement /
// Classification / Section-override controls on the Documents tab.
function flattenTree(nodes: readonly SectionNode[]): SectionOption[] {
  const out: SectionOption[] = [];
  const walk = (node: SectionNode): void => {
    out.push({ code: node.code, label: node.label });
    for (const c of node.children) walk(c);
  };
  for (const n of nodes) walk(n);
  return out;
}

const groupHeaderStyle: CSSProperties = {
  margin: '14px 0 4px 0',
  padding: '6px 10px',
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--text)',
};

// phase-compiler-tab-pins (layout-followup Item A) — the Bookmarks & Links
// document ◀▶ navigator buttons. Disabled at the ends (`:disabled` dims the
// native control) so the walk never wraps.
const bookmarksNavButtonStyle: CSSProperties = {
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--text)',
  cursor: 'pointer',
};

/**
 * The compiler screen, scoped to ONE submission. Exported only through the
 * route-keyed wrapper below: every piece of per-submission UI state
 * (`useSubmissionViewState` — selected section/document, active tab, tree
 * pin/show) HYDRATES ONCE on mount and never re-reads its key, so sharing a
 * single mounted instance across two different `:submissionId` routes would
 * leak the first submission's selections and in-flight forms into the second.
 * The wrapper keys this inner component by the route id, forcing a fresh mount
 * (and fresh hydration) on every submission change.
 */
function CompilerPageInner({ submissionId }: { readonly submissionId?: string }): ReactElement {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, loading: permissionsLoading } = useUserPermissions();

  // Wave E.2 — Inspector write actions (placement, classification approval)
  // gate on Documents:write. Read-only viewers see the inspector tabs
  // but the action buttons are disabled.
  const canWrite = hasPermission('Documents', 'write');
  // T-17 (T17-D9) — POST /compile (and editing the submission) need
  // Submissions:write, a different permission from Documents:write. Gating
  // Compile on Documents:write offered a reviewer a button that could only end
  // in a 403. Users without it get no Compile controls at all, plus a note.
  const canWriteSubmission = hasPermission('Submissions', 'write');
  // T-07 (D-27) — approving a classification is a sign-off: the server checks
  // Documents.sign (POST /documents/:id/classification/approve), so the
  // inspector's Approve controls follow Documents.sign, not Documents.write.
  const canSign = hasPermission('Documents', 'sign');

  // Phase settings-continuous-validation (TODO-074) — read the per-user
  // continuousValidation setting to control toolbar Re-validate button.
  // defaultValue=false (schema) so the button is shown until the user
  // explicitly turns continuous validation on.
  // T-17 (D6): read for THIS submission, so a submission-level override applies.
  const { value: continuousValidationOn } = useSetting<boolean>('continuousValidation', {
    submissionId,
  });

  // T-17 (T17-D2) — publish governance (`preP`/`reAuth`) is NOT read here any
  // more. The backend resolves it for THIS submission (its own company profile
  // -> org setting -> false) and serves it with the checklist items; the
  // PublishWizard fetches it itself (`usePublishGovernance`) and renders only
  // once it has resolved. Reading the two org settings in the page let the
  // wizard disagree with what POST /compile enforces.

  // Phase compiler-undo-stack (TODO-057) — 20-entry client-side snapshot ring buffer.
  const undoStack = useUndoStack();

  // TODO-081 — cross-section drag reclassification: toast + dialog state.
  const { toast, showToast, clearToast } = useUiToast();
  const [reclassifyTarget, setReclassifyTarget] = useState<{
    documentId: string;
    documentName: string;
    fromSection: string;
    toSection: string;
    position?: DocumentDropPosition;
  } | null>(null);

  const [submission, setSubmission] = useState<AsyncState<Submission>>(initialAsync<Submission>());
  // Both guards resolve to the same condition (writable && not sequence-locked)
  // but are consumed in distinct contexts: `canWriteTree` gates CTD-tree
  // drop/move/insert operations (precise insertion capability), while
  // `canEditDocuments` gates per-document edit/classify/approve surfaces.
  const canWriteTree = canWrite && submission.data !== null && !isSequenceLocked(submission.data);
  const canEditDocuments = canWrite && submission.data !== null && !isSequenceLocked(submission.data);
  const [tree, setTree] = useState<AsyncState<SectionTreeResponse>>(initialAsync<SectionTreeResponse>());
  const [documents, setDocuments] = useState<AsyncState<readonly Document[]>>(
    initialAsync<readonly Document[]>(),
  );

  // Wave E.4 — Submission-wide validation results for the top banner.
  const [validation, setValidation] = useState<AsyncState<SubmissionValidationResultsResponse>>(
    initialAsync<SubmissionValidationResultsResponse>(),
  );
  const statusEvidence = useStatusEvidence(submissionId ? [submissionId] : [], validation, documents.data, submission.data);
  const [validatingNow, setValidatingNow] = useState(false);

  // Phase phase-publish-as-mode (TODO-022) — wizard mode. When true the
  // whole pinnable-tab Workspace (CTD tree + task panels) is replaced by an
  // in-place 4-step PublishWizard. Click "Compile" in CompileSubmissionPanel to
  // enter; the wizard's Cancel or Done exits.
  const [publishMode, setPublishMode] = useState(false);

  // phase-compiler-tab-pins — the active Workspace tab selection (CTD Tree /
  // Documents / Review / Bookmarks & Links / Validation / Lifecycle / Package).
  // It is persisted per-submission; the storage layer already
  // migrates a legacy activePane / inspectorTab / activeMiddleTab into it and
  // never leaves it blank (resolveWorkspaceTab), so the guard here is only
  // belt-and-suspenders against a hand-edited sessionStorage value.
  const [savedWorkspaceTab, setWorkspaceTab] = useSubmissionViewState<WorkspaceTabKey>(submissionId ?? '', 'workspaceTab', 'documents');
  const workspaceTab: WorkspaceTabKey = isWorkspaceTab(savedWorkspaceTab) ? savedWorkspaceTab : 'documents';
  // phase-compiler-tab-pins — the CTD tree is now a first-class pinnable tab and
  // EVERY tab carries a pin. A pinned tab stays visible while another is active;
  // the panes on screen are the active+pinned UNION. The pin set is a canonical
  // comma-joined string the storage layer (resolveWorkspacePins) hydrates and
  // migrates (legacy pinnedPanes / treePinned) and never leaves blank — a new
  // session has the tree pinned. Parse it into a set for the shell and toggle
  // through the canonical serializer so one evaluator governs the stored shape.
  const [savedPins, setPins] = useSubmissionViewState<string>(submissionId ?? '', 'workspacePins', serializeWorkspacePins(DEFAULT_WORKSPACE_PINS));
  const pinnedTabs = useMemo(() => parseWorkspacePins(savedPins), [savedPins]);

  // Bookmarks & Links is "visible" when it is the active tab OR pinned beside another.
  // A navigation-ENTRY generation is bumped SYNCHRONOUSLY (render time) on each
  // hidden→visible transition, so the capability hook sees a re-entry as a new
  // generation and `stale` is true that same commit — BEFORE the auto-select effect
  // runs — preventing selection from a prior generation's map (Kimi closeout G1).
  const bookmarksVisible = workspaceTab === 'bookmarks-links' || pinnedTabs.has('bookmarks-links');
  const bookmarksVisibleRef = useRef(false);
  const navEntryGenRef = useRef(0);
  if (bookmarksVisible && !bookmarksVisibleRef.current) navEntryGenRef.current += 1;
  bookmarksVisibleRef.current = bookmarksVisible;
  const navEntryGen = navEntryGenRef.current;
  const togglePin = useCallback((key: WorkspaceTabKey) => {
    setPins((prev) => {
      const next = parseWorkspacePins(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return serializeWorkspacePins(next);
    });
  }, [setPins]);
  // The tree column's fraction of the usable width when it shares a wide row
  // with non-tree panes. Default is an equal split (half), adapting to the
  // viewport inside CompilerWorkspace rather than a hardcoded pixel width;
  // an explicit drag/keyboard adjustment persists this ratio per submission.
  const [treeRatio, setTreeRatio] = useSubmissionViewState<number>(submissionId ?? '', 'treeRatio', 0.5);
  // phase-compiler-tab-pins (layout-followup Item B) — the workspace VIEW PRESET,
  // persisted per-submission alongside the pins/ratio. The pins decide WHICH
  // panes are visible; the preset decides their PROPORTIONS (two-equal 50/50,
  // thirds, or half-quarters 50/25/25). parseViewPreset validates a hand-edited
  // or legacy value back to the default. Selecting a preset also RESETS the tree
  // ratio to that preset's natural split so the proportions match immediately
  // (half-quarters/two-equal → 0.5, thirds → 1/3) — one canonical model in
  // workspaceLayout.ts governs both the ratio default and the non-tree grid.
  const [savedViewPreset, setViewPresetRaw] = useSubmissionViewState<string>(submissionId ?? '', 'viewPreset', DEFAULT_VIEW_PRESET);
  const viewPreset: WorkspaceViewPreset = parseViewPreset(savedViewPreset);
  const selectPreset = useCallback((preset: WorkspaceViewPreset) => {
    setViewPresetRaw(preset);
    setTreeRatio(presetDefaultTreeRatio(preset));
  }, [setViewPresetRaw, setTreeRatio]);

  // Contextual sub-views WITHIN a tab (NOT main tabs): Documents (list/classify)
  // and Validation (submission/document). Local state — these are view toggles,
  // not durable selections. Review's own preview/compare/annotations selector is
  // owned by WorkspaceReviewPanel (B).
  const [docsView, setDocsView] = useState<'list' | 'classify'>('list');
  const [validationView, setValidationView] = useState<'submission' | 'document'>('submission');
  // User-requested (2026-10-28 browser review): a control that actually HIDES
  // the process-status strip (the old "View all stages" only toggled stage
  // detail, never removed the band). When hidden, both the status pipeline and
  // the compile/validate/report action strip collapse to reclaim vertical space
  // for the six-tab workspace; a single compact "Validation" link remains so the
  // worklist is still one click away. Persisted per-submission; defaults open so
  // compile/report stay visible until the operator chooses to hide them.
  const [processStatusOpen, setProcessStatusOpen] = useSubmissionViewState<boolean>(submissionId ?? '', 'processStatusOpen', true);
  // phase-compiler-six-tabs — bumped after a submission navigation-policy
  // snapshot is applied so the kept-mounted NavigationTab re-reads its envelope
  // (and unlocks) without being remounted, which would drop a staged reason.
  const [navRefreshNonce, setNavRefreshNonce] = useState(0);

  // Phase compiler-edit-submission-metadata (TODO-062) — modal toggle.
  const [editingMetadata, setEditingMetadata] = useState(false);

  const workspace = useSubmissionWorkspace();
  const { ask: askDiscardDraft, dialog: draftDialog } = useConfirmation();
  // phase-compiler-six-tabs — generic unsaved-change protection for a CONTEXT
  // change (switching the selected document / section). A main-tab switch does
  // NOT call this: Bookmarks & Links stays mounted across tab switches, so its
  // navigation draft is never at risk — guarding a tab switch would discard
  // edits that nothing is unmounting. This guard fires only when the thing the
  // draft belongs to is actually about to change underneath it.
  const mayChangeContext = useCallback(async () => {
    if (!workspace?.draftSources?.size) return true;
    if ([...workspace.draftSources].some((source) => source.busy)) {
      await askDiscardDraft({ title: 'Save in progress', message: 'Wait for the save to finish before switching.', confirmLabel: 'Stay here' });
      return false;
    }
    const accepted = await askDiscardDraft({ title: 'Unsaved changes', message: 'Discard your unsaved changes and switch?', confirmLabel: 'Discard and switch', variant: 'destructive' });
    if (accepted) for (const source of workspace.draftSources) source.discard?.();
    return accepted;
  }, [workspace, askDiscardDraft]);

  const [validationReportOpen, setValidationReportOpen] = useState(false);
  const [agencyConfirmOpen, setAgencyConfirmOpen] = useState(false);
  const [agencyConfirmBusy, setAgencyConfirmBusy] = useState(false);
  const [priorWarning, setPriorWarning] = useState<{
    sequenceNumber: string;
    status: string;
  } | null>(null);
  const priorCheckedRef = useRef<string | null>(null);

  const [selectedSection, setSelectedSection] = useSubmissionViewState<string | null>(submissionId ?? '', 'selectedSection', null);
  const [selectedDocumentId, setSelectedDocumentId] = useSubmissionViewState<string | null>(submissionId ?? '', 'selectedDocumentId', null);

  // D3 — one auto-select per "Bookmarks & Links became visible" session, reset when
  // it hides. Prevents overriding a deliberate selection made WHILE the tab is open
  // and prevents the discard-then-reselect loop after a guarded section change.
  const bookmarksAutoSelectDoneRef = useRef(false);
  /**
   * phase-compiler-six-tabs — the Review tab's viewer PAGE, lifted to the page
   * so it survives main-tab switches and resets to 1 on document change. It is
   * passed ONLY to WorkspaceReviewPanel. Deliberately NOT shared with the
   * Bookmarks & Links exact-revision viewer, which owns its own page/focus over
   * the navigation DRAFT bytes — a different artifact from Review's source-file
   * preview, so coupling their page positions would be wrong.
   */
  const [viewerPage, setViewerPage] = useState<number>(1);
  // A new document starts at page 1 — carrying page 7 onto a 2-page file would
  // be carrying state that has no meaning for it.
  useEffect(() => {
    setViewerPage(1);
  }, [selectedDocumentId]);

  // Track whether the user-selected document is still in the active list —
  // if they switch sections and the doc disappears, clear the selection so the
  // document-scoped panels fall back to their empty states.
  // Phase phase-review-surface-strengthen — all-document summary for CTD tree
  // review-state signals. Fetched once per submission (lightweight read-only
  // call); reloaded when documents change. Used only to compute the sets of
  // section codes with unconfirmed / low-confidence placements.
  const [allDocsForTree, setAllDocsForTree] = useState<readonly Document[]>([]);
  const [allDocsLoading, setAllDocsLoading] = useState(true);
  const [allDocsError, setAllDocsError] = useState<string | null>(null);
  // phase-ui-compiler-tree-documents-pane — the grouped all-documents default
  // view mutates rows while no section is selected (reloadDocuments early-
  // returns there), so it bumps this key to refresh allDocsForTree directly.
  const [allDocsReloadKey, setAllDocsReloadKey] = useState(0);
  const reloadAllDocs = useCallback(() => setAllDocsReloadKey((k) => k + 1), []);

  // phase-compiler-tab-pins (Kimi D2/D3) — authoritative, server-computed per-doc
  // navigation capability for this submission (the Bookmarks & Links candidate set).
  // `allDocsReloadKey` is the invalidation trigger: every successful document reload
  // (upload, conversion refresh, reclassification, undo/redo, snapshot capture) bumps
  // it, so the capability map refetches instead of going stale. While it refreshes,
  // `navCapability.stale` disables auto-select + Previous/Next so neither acts on
  // known-stale candidates; the last map is retained so an open editor is preserved.
  const navCapability = useNavigationCapability(submissionId, allDocsReloadKey, navEntryGen);
  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    setAllDocsLoading(true);
    setAllDocsError(null);
    listAllSubmissionDocuments(submissionId).then((docs) => {
      if (!cancelled) setAllDocsForTree(docs);
    }).catch(() => {
      if (!cancelled) setAllDocsError('Could not load documents for the current sequence.');
    }).finally(() => {
      if (!cancelled) setAllDocsLoading(false);
    });
    return () => { cancelled = true; };
  }, [submissionId, documents.data, allDocsReloadKey]); // re-fetch when section-docs change (confirming, reclassifying)

  const selectedDocument = useMemo(() => {
    if (!selectedDocumentId) return null;
    // phase-ui-compiler-tree-documents-pane — in the grouped all-documents
    // view no section is selected, so documents.data is null; resolve the
    // clicked row from allDocsForTree so the inspector still opens.
    return (
      documents.data?.find((d) => d.Id === selectedDocumentId) ??
      allDocsForTree.find((d) => d.Id === selectedDocumentId) ??
      null
    );
  }, [documents.data, allDocsForTree, selectedDocumentId]);

  // phase-ui-compiler-tree-document-leaves — bucket the already-fetched
  // all-documents list by sectionKey for the tree's document leaves (no
  // second query; same source as the grouped middle-pane view).
  const treeDocBuckets = useMemo(() => {
    const byKey = new Map<string, Document[]>();
    const unplaced: Document[] = [];
    for (const d of allDocsForTree) {
      const key = sectionKey(d.CtdSectionCode ?? d.Classification ?? '');
      if (key === '') {
        unplaced.push(d);
      } else {
        const list = byKey.get(key) ?? [];
        list.push(d);
        byKey.set(key, list);
      }
    }
    return { byKey, unplaced };
  }, [allDocsForTree]);

  // Replace/delete targets live in PRIOR sequences, so their sequence
  // numbers aren't in this submission's document list. One small fetch of
  // the prior-documents catalog resolves ReplacesDocumentId → "seq 0000"
  // for the leaf annotations; skipped entirely when no doc links a prior.
  const [priorSeqByDocId, setPriorSeqByDocId] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    if (!submissionId) return;
    if (!allDocsForTree.some((d) => d.ReplacesDocumentId)) {
      setPriorSeqByDocId(new Map());
      return;
    }
    let cancelled = false;
    v2.submissions
      .getPriorDocuments(submissionId)
      .then((priors) => {
        if (cancelled) return;
        const m = new Map<string, string>();
        for (const p of priors as Array<Document & { SubSequenceNumber?: string }>) {
          if (p.SubSequenceNumber) m.set(p.Id, p.SubSequenceNumber);
        }
        setPriorSeqByDocId(m);
      })
      .catch(() => {
        /* annotation-only — leaves degrade to "prior linked" */
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, allDocsForTree]);

  // -- Load submission + tree on mount / id change ----------------------------
  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    setSubmission({ data: null, loading: true, error: null });
    setTree({ data: null, loading: true, error: null });

    void Promise.allSettled([v2.submissions.get(submissionId), compilerApi.sectionTree(submissionId)]).then(
      ([subRes, treeRes]) => {
        if (cancelled) return;
        if (subRes.status === 'fulfilled') {
          setSubmission({ data: subRes.value, loading: false, error: null });
        } else {
          setSubmission({ data: null, loading: false, error: errorMessage(subRes.reason) });
        }
        if (treeRes.status === 'fulfilled') {
          setTree({ data: treeRes.value, loading: false, error: null });
        } else {
          setTree({ data: null, loading: false, error: errorMessage(treeRes.reason) });
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  // -- Load documents whenever the selected section changes -------------------
  // Wave E.1 — keep a request id so out-of-order responses don't clobber state.
  const docReqIdRef = useRef(0);
  useEffect(() => {
    if (!submissionId) return;
    if (!selectedSection) {
      setDocuments({ data: null, loading: false, error: null });
      return;
    }
    const reqId = ++docReqIdRef.current;
    setDocuments({ data: null, loading: true, error: null });
    compilerApi
      .documentsBySection(submissionId, selectedSection)
      .then((docs) => {
        if (reqId !== docReqIdRef.current) return;
        setDocuments({ data: docs, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (reqId !== docReqIdRef.current) return;
        setDocuments({ data: null, loading: false, error: errorMessage(err) });
      });
  }, [submissionId, selectedSection]);

  // -- When the user picks a new section, drop any stale document selection ---
  const handleSelectSection = useCallback(async (code: string | null) => {
    if (code === selectedSection && !selectedDocumentId) return;
    if (workspace?.draftSources?.size && !(await mayChangeContext())) return;
    // D3 — an explicit section choice while Bookmarks & Links is open is the user
    // taking control: do NOT let the first-applicable effect re-select a document
    // underneath it (that is the discard-then-reselect loop the review flagged).
    if (bookmarksVisibleRef.current) bookmarksAutoSelectDoneRef.current = true;
    setSelectedSection(code);
    // item 5 — a SECTION click clears any document highlight: the section
    // becomes the tree's one primary selection.
    setSelectedDocumentId(null);
  }, [setSelectedDocumentId, setSelectedSection, selectedSection, selectedDocumentId, workspace, mayChangeContext]);

  // item 5 — map sectionKey → the tree node's own code, so a document click
  // can resolve its CONTAINING section to the code the tree/fetch layer uses.
  const sectionCodeByKey = useMemo(() => {
    const m = new Map<string, string>();
    const walk = (nodes: readonly SectionNode[]): void => {
      for (const n of nodes) {
        m.set(sectionKey(n.code), n.code);
        if (n.children.length > 0) walk(n.children);
      }
    };
    walk(tree.data?.tree ?? []);
    return m;
  }, [tree.data]);

  // phase-compiler-chrome-batch item 5 — ONE selection model. A document
  // click selects the document AND implies its containing section: the
  // Workspace focuses that section's list and the Inspector shows the
  // document, from one click. The tree then paints exactly one PRIMARY
  // highlight (the document), with a muted tint on the containing section
  // (SectionTree renders the tint whenever a document holds primary).
  // Previously the two highlights were independent and accumulated.
  // The unguarded core of a document selection: records the id and resolves its
  // containing section. Extracted so the first-applicable auto-select (Bookmarks &
  // Links, layout-followup Item A) can reuse the exact same section resolution
  // WITHOUT a draft-discard prompt. Prompting there would be spurious because the
  // effect's own gates already make a prompt unnecessary (Kimi D3): it is SUPPRESSED
  // whenever ANY workspace draft is live (rename/lifecycle/navigation — draftSources
  // is workspace-wide, not navigation-only) and fires at most once per visible
  // session, so it can never switch out from under unsaved work or override a
  // deliberate selection. Explicit selections go through the GUARDED
  // handleSelectDocument / handleSelectSection instead.
  const selectDocumentUnguarded = useCallback(
    (docId: string) => {
      setSelectedDocumentId(docId);
      // phase-compiler-six-tabs — selecting a document updates the shared
      // selected-file header and every document-scoped tab in place; it does
      // NOT force a main-tab switch (the six tabs stay where the operator put
      // them, so Lifecycle/Validation/etc. remain where they expect).
      const doc =
        documents.data?.find((d) => d.Id === docId) ??
        allDocsForTree.find((d) => d.Id === docId);
      const key = doc ? sectionKey(doc.CtdSectionCode ?? doc.Classification ?? '') : '';
      // Unplaced (or a code the tree doesn't model) → the grouped all-
      // documents view is the honest "containing section".
      setSelectedSection(key ? (sectionCodeByKey.get(key) ?? null) : null);
    },
    [documents.data, allDocsForTree, sectionCodeByKey, setSelectedDocumentId, setSelectedSection],
  );

  const handleSelectDocument = useCallback(
    async (docId: string) => {
      if (workspace?.draftSources?.size && !(await mayChangeContext())) return;
      // D3 — a deliberate document choice (incl. an ineligible one, or Previous/Next)
      // made while Bookmarks & Links is open is the user taking control: the first-
      // applicable effect must never override it later (e.g. on a late capability load).
      if (bookmarksVisibleRef.current) bookmarksAutoSelectDoneRef.current = true;
      selectDocumentUnguarded(docId);
    },
    [selectDocumentUnguarded, workspace, mayChangeContext],
  );

  // Opening a leaf in another sequence enters its own compiler context first;
  // never run a mutation for a sibling document under the current submission.
  useEffect(() => {
    const documentId = searchParams.get('documentId');
    if (!documentId || !allDocsForTree.some((doc) => doc.Id === documentId)) return;
    selectDocumentUnguarded(documentId);
    const next = new URLSearchParams(searchParams);
    next.delete('documentId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, allDocsForTree, selectDocumentUnguarded]);

  // Phase compiler-layout-enhancements TODO-064 — navigate-to-doc helper
  // used by the bottom notification drawer chips. Selects the doc and scrolls
  // its row into view if it's in the current section list. If the doc lives in
  // a different section the selection still records; the row may not exist yet
  // — the user will have to switch sections to see it. (Cross-section
  // navigation is a future enhancement; current API only returns documents for
  // the currently-selected section.)
  const handleNavigateToDocument = useCallback(
    async (docId: string): Promise<boolean> => {
      // D3 — a notification / validation "Fix" jump is a deliberate user choice. Mark
      // intent BEFORE the awaited guard/fetch so a capability map that resolves during
      // the await cannot auto-select the first-applicable document over this jump while
      // Bookmarks & Links is visible. (If the guard then cancels, the selection simply
      // does not change; the ref only suppresses an UNRELATED auto-takeover.)
      if (bookmarksVisibleRef.current) bookmarksAutoSelectDoneRef.current = true;
      if (workspace?.draftSources?.size && !(await mayChangeContext())) return false;
      const scrollToRow = (): void => {
        globalThis.requestAnimationFrame(() => {
          const row =
            document.querySelector<HTMLElement>(`[data-testid="document-row-${docId}"]`) ??
            document.querySelector<HTMLElement>(`[data-testid="doc-row-${docId}"]`) ??
            document.querySelector<HTMLElement>(`[data-testid="document-list-row-${docId}"]`);
          row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      };

      // Fast path: doc already in the active section list — no fetch needed.
      const inList = documents.data?.some((d) => d.Id === docId);
      if (inList) {
        setSelectedDocumentId(docId);
        scrollToRow();
        return true;
      }

      // Phase compiler-inline-fix-validation (TODO-023) — cross-section
      // jump. Fetch the doc to learn its CtdSectionCode, switch the active
      // section, then queue the selection so it happens AFTER the new
      // section's docs load. The docs-loader useEffect handles the actual
      // fetch; we just stash the pending id.
      await v2.documents
        .get(docId)
        .then((doc) => {
          const sec = (doc.CtdSectionCode as string | null | undefined) ?? null;
          if (sec && sec !== selectedSection) {
            setSelectedSection(sec);
          }
          setSelectedDocumentId(docId);
          // Best-effort scroll — the row might not yet exist if the new
          // section's docs are still loading. The selection is sticky so
          // re-render after load will give it isSelected styling.
          scrollToRow();
        })
        .catch(() => {
          // Silent — the user clicked Fix on a doc the server couldn't
          // return. Selecting the id alone is harmless; inspector will
          // just sit empty.
          setSelectedDocumentId(docId);
        });
      return true;
    },
    [documents.data, selectedSection, setSelectedDocumentId, setSelectedSection, workspace, mayChangeContext],
  );

  // phase-compiler-six-tabs — a Links-worklist row click: navigate to the
  // source document, then open the Validation tab on its per-document view
  // (where the link-validation inventory now lives). Replaces the old
  // focus-nonce that snapped the Inspector's internal tab.
  const handleOpenDocumentLinks = useCallback(
    async (docId: string): Promise<void> => {
      if (await handleNavigateToDocument(docId)) {
        setWorkspaceTab('validation');
        setValidationView('document');
      }
    },
    [handleNavigateToDocument, setWorkspaceTab],
  );

  // phase-compiler-six-tabs — the chrome's compact validation summary (banner
  // "open worklist" + toolbar badge) no longer opens a bottom drawer; it switches
  // to the Validation main tab on its submission scope, where the worklist now
  // lives embedded. One always-present home for validation, never a duplicate.
  const openValidationSubmission = useCallback((): void => {
    setWorkspaceTab('validation');
    setValidationView('submission');
  }, [setWorkspaceTab]);

  // phase-compiler-six-tabs — the Bookmarks & Links editor's "show links" chip
  // opens the Validation tab on the SELECTED-document scope (where the per-
  // document link inventory now lives). The document is already selected, so
  // this only switches tab + scope.
  const handleOpenSelectedDocumentLinks = useCallback((): void => {
    setWorkspaceTab('validation');
    setValidationView('document');
  }, [setWorkspaceTab]);

  // -- Wave E.2 — Reload the documents list after an inspector write so the
  // changed CtdSectionCode / ClassificationApprovedAt etc. surface in the
  // middle-pane list immediately. Re-runs the section-scoped fetch.
  const reloadDocuments = useCallback(() => {
    if (!submissionId) return;
    if (!selectedSection) {
      reloadAllDocs();
      return;
    }
    const reqId = ++docReqIdRef.current;
    compilerApi
      .documentsBySection(submissionId, selectedSection)
      .then((docs) => {
        if (reqId !== docReqIdRef.current) return;
        setDocuments({ data: docs, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (reqId !== docReqIdRef.current) return;
        setDocuments({ data: null, loading: false, error: errorMessage(err) });
      });
  }, [submissionId, selectedSection, reloadAllDocs]);

  // -- Wave E.4 — Validation results for the submission-level banner.
  // Fetched on initial load + after every "Validate all" run.
  const fetchValidation = useCallback(async (): Promise<void> => {
    if (!submissionId) return;
    setValidation((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const next = await v2.submissions.getValidationResults(submissionId);
      setValidation({ data: next, loading: false, error: null });
    } catch (err) {
      setValidation({ data: null, loading: false, error: errorMessage(err) });
    }
  }, [submissionId]);

  useEffect(() => {
    if (!submissionId) return;
    void fetchValidation();
  }, [submissionId, fetchValidation]);

  // Phase UI-13 — sequence-warning detection. Fetches prior sequences
  // once per submissionId; surfaces the warning modal if any prior has
  // Status !== 'Complete'.
  useEffect(() => {
    if (!submissionId) return;
    if (priorCheckedRef.current === submissionId) return;
    priorCheckedRef.current = submissionId;
    v2.submissions
      .priorSequences(submissionId)
      .then((res) => {
        const offender = (res ?? []).find((r) => r.Status !== 'Complete');
        if (offender) {
          setPriorWarning({
            sequenceNumber: offender.SequenceNumber,
            status: offender.Status,
          });
        }
      })
      .catch(() => {
        // Non-fatal — silently skip the warning. Compiler is still usable.
      });
  }, [submissionId]);

  // Phase UI-13 — agency confirmation handler.
  const handleAgencyConfirm = useCallback(async (): Promise<void> => {
    if (!submissionId) return;
    setAgencyConfirmBusy(true);
    try {
      const next = await v2.submissions.agencyConfirm(submissionId);
      setSubmission({ data: next, loading: false, error: null });
      setAgencyConfirmOpen(false);
    } catch (err) {
      setSubmission((prev) => ({ ...prev, error: errorMessage(err) }));
    } finally {
      setAgencyConfirmBusy(false);
    }
  }, [submissionId]);

  // Phase UI-13 — reload submission after unlock.
  const reloadSubmission = useCallback(async (): Promise<void> => {
    if (!submissionId) return;
    try {
      const next = await v2.submissions.get(submissionId);
      setSubmission({ data: next, loading: false, error: null });
    } catch (err) {
      setSubmission((prev) => ({ ...prev, error: errorMessage(err) }));
    }
  }, [submissionId]);
  useEffect(() => subscribeSubmissionRefresh(() => { void reloadSubmission(); }), [reloadSubmission]);

  const handleValidateAll = useCallback(async (): Promise<void> => {
    if (!submissionId) return;
    setValidatingNow(true);
    try {
      const next = await v2.submissions.validateNow(submissionId);
      setValidation({ data: next, loading: false, error: null });
    } catch (err) {
      setValidation((prev) => ({ ...prev, error: errorMessage(err) }));
    } finally {
      setValidatingNow(false);
    }
  }, [submissionId]);

  // Serialize tree drop gestures so a later upload cannot overwrite an earlier order.
  const treeDropQueue = useRef<Promise<void>>(Promise.resolve());
  const queueTreeDrop = useCallback((operation: () => Promise<void>): void => {
    treeDropQueue.current = treeDropQueue.current.then(operation).catch((error: unknown) => {
      showToast({ type: 'error', text: errorMessage(error) });
    }).finally(() => {
      reloadDocuments();
      reloadAllDocs();
    });
  }, [reloadDocuments, reloadAllDocs, showToast]);

  const handleDropFiles = useCallback((sectionCode: string, files: readonly File[], position?: DocumentDropPosition): void => {
    if (!submissionId || !canWriteTree) return;
    queueTreeDrop(() => uploadDroppedFiles(submissionId, sectionCode, files, position, (file, error) => {
      showToast({ type: 'error', text: `Could not upload ${file.name}. ${error instanceof ApiError ? error.message : 'Please try dropping the file again.'}` });
    }));
  }, [submissionId, canWriteTree, queueTreeDrop, showToast]);

  const handleDropDocument = useCallback(
    (documentId: string, targetSectionCode: string, position?: DocumentDropPosition): void => {
      if (!submissionId || !canWriteTree) return;
      const doc = allDocsForTree.find((d) => d.Id === documentId) ?? documents.data?.find((d) => d.Id === documentId);
      if (!doc) return;
      if (doc.IsLocked === true) {
        showToast({ type: 'error', text: 'Unlock document before moving.' });
        return;
      }
      const fromSection = doc.CtdSectionCode ?? doc.Classification ?? '';
      if (sectionKey(fromSection) === sectionKey(targetSectionCode)) {
        if (!position || position.documentId === documentId) return;
        queueTreeDrop(async () => {
          const originalOrder = await orderDroppedDocuments(submissionId, targetSectionCode, [documentId], position);
          undoStack.push({
            description: `Reorder documents in ${targetSectionCode}`,
            undo: async () => {
              await v2.documentReorder.reorder(submissionId, originalOrder);
              reloadDocuments();
              reloadAllDocs();
            },
          });
        });
        return;
      }
      setReclassifyTarget({
        documentId: doc.Id,
        documentName: doc.FileName,
        fromSection: fromSection || '(unplaced)',
        toSection: targetSectionCode,
        ...(position ? { position } : {}),
      });
    },
    [submissionId, canWriteTree, allDocsForTree, documents.data, showToast, queueTreeDrop, undoStack, reloadDocuments, reloadAllDocs],
  );

  const handleReclassifyConfirm = useCallback(async (): Promise<void> => {
    if (!reclassifyTarget || !submissionId) return;
    const { documentId, documentName, fromSection, toSection, position } = reclassifyTarget;
    setReclassifyTarget(null);
    queueTreeDrop(async () => {
      // Resolve the target and lock guards before changing placement.
      await readDropTarget(submissionId, toSection, position);
      const original = await v2.documents.get(documentId);
      if (original.IsLocked) throw new Error('Unlock document before moving.');
      const restorePlacement = async (): Promise<void> => {
        if (original.CtdSectionCode) {
          await v2.documents.updatePlacement(documentId, { newSectionCode: original.CtdSectionCode });
          const restored = await v2.documents.get(documentId);
          await v2.documents.update(documentId, { SortOrder: original.SortOrder }, restored.RowVersion);
        } else {
          const current = await v2.documents.get(documentId);
          await v2.documents.update(documentId, { CtdSectionCode: original.CtdSectionCode ?? null, SortOrder: original.SortOrder }, current.RowVersion);
        }
      };
      await v2.documents.updatePlacement(documentId, { newSectionCode: toSection });
      try {
        await orderDroppedDocuments(submissionId, toSection, [documentId], position);
      } catch (error) {
        // Preserve the prior placement if the separate reorder request fails.
        try { await restorePlacement(); } catch {
          throw new Error(`Document moved to ${toSection}, but its order could not be saved or its prior placement restored. Please review its placement.`);
        }
        throw error;
      }
      undoStack.push({
        description: `Move "${documentName}" back to ${fromSection}`,
        undo: async () => {
          await restorePlacement();
          reloadDocuments();
          reloadAllDocs();
          void fetchValidation();
        },
      });
      void fetchValidation();
    });
  }, [reclassifyTarget, submissionId, queueTreeDrop, reloadDocuments, reloadAllDocs, fetchValidation, undoStack]);

  // phase-classification-model-selection — the Classification tab's classify
  // affordances, wired so the panel's model selector actually governs a run.
  // Both pass the selected model as a REQUEST; the server re-resolves
  // precedence and clamps to the tenant max tier, and echoes back what ran —
  // including an honest note when the choice was capped.
  const handleReclassifyDocument = useCallback(
    (documentId: string, modelId?: string): void => {
      void (async () => {
        try {
          const result = await v2.documents.classify(documentId, {
            ...(modelId ? { model: modelId } : {}),
          });
          showToast({
            type: 'success',
            text:
              result.modelNote ??
              `Classified as ${result.Classification} using ${
                result.modelDisplayName ?? result.model
              }.`,
          });
          reloadDocuments();
        } catch (err) {
          showToast({ type: 'error', text: errorMessage(err) });
        }
      })();
    },
    [reloadDocuments, showToast],
  );

  const handleClassifyAll = useCallback(
    (modelId?: string): void => {
      if (!submissionId) return;
      void (async () => {
        try {
          const res = await v2.submissions.classifyAllStream(
            submissionId,
            undefined,
            modelId ?? null,
          );
          const body = (await res.json()) as {
            ok?: boolean;
            error?: string;
            message?: string;
            data?: { totalDocs?: number; modelDisplayName?: string; modelNote?: string | null };
          };
          if (!res.ok || body.ok === false) {
            showToast({ type: 'error', text: body.message ?? body.error ?? 'Classify all failed.' });
            return;
          }
          showToast({
            type: 'success',
            text:
              body.data?.modelNote ??
              `Classifying ${body.data?.totalDocs ?? 0} document(s) with ${
                body.data?.modelDisplayName ?? 'the default model'
              }.`,
          });
        } catch (err) {
          showToast({ type: 'error', text: errorMessage(err) });
        }
      })();
    },
    [submissionId, showToast],
  );

  // Phase compiler-undo-stack (TODO-057) — Ctrl+Z keyboard binding.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if ((e.target as HTMLElement).isContentEditable) return;
        e.preventDefault();
        if (undoStack.canUndo) {
          const entry = undoStack.pop();
          if (entry) {
            entry.undo().catch((err: unknown) => {
              // Phase audit-fixes-bundle (M-02) — re-push on failure so the
              // user can retry, otherwise subsequent Ctrl+Z undoes a stale,
              // older action and the failed action's state is permanently
              // out of sync.
              undoStack.push(entry);
              showToast({
                type: 'error',
                text: `Undo failed: ${err instanceof Error ? err.message : String(err)}`,
              });
            });
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undoStack, showToast]);

  // Phase compiler-batch-operations — undo callbacks forwarded from DocumentList.
  const handleBatchConfirmed = useCallback((ids: string[]): void => {
    undoStack.push({
      description: `Confirm ${ids.length} document${ids.length !== 1 ? 's' : ''}`,
      undo: async () => {
        await Promise.all(ids.map((id) => v2.documents.unconfirm(id)));
        reloadDocuments();
        reloadAllDocs();
      },
    });
  }, [undoStack, reloadAllDocs, reloadDocuments]);

  const handleBatchLocked = useCallback((ids: string[], locked: boolean): void => {
    undoStack.push({
      description: `${locked ? 'Lock' : 'Unlock'} ${ids.length} document${ids.length !== 1 ? 's' : ''}`,
      undo: async () => {
        await v2.documents.batchSetLock(submissionId ?? '', ids, !locked);
        reloadDocuments();
        reloadAllDocs();
      },
    });
  }, [undoStack, reloadAllDocs, reloadDocuments, submissionId]);

  // Phase undo-stack-remaining-mutations — individual lock toggle undo.
  const handleLockToggled = useCallback(
    (docId: string, docName: string, wasLocked: boolean): void => {
      undoStack.push({
        description: `${wasLocked ? 'Unlock' : 'Lock'} "${docName}"`,
        undo: async () => {
          await v2.documents.setLock(docId, wasLocked);
          reloadDocuments();
        reloadAllDocs();
        },
      });
    },
    [undoStack, reloadAllDocs, reloadDocuments],
  );

  // Phase undo-stack-remaining-mutations — reorder undo (restore original order).
  const handleReordered = useCallback(
    (originalOrderIds: string[]): void => {
      if (!submissionId) return;
      undoStack.push({
        description: selectedSection ? `Reorder documents in ${selectedSection}` : 'Reorder documents',
        undo: async () => {
          await v2.documentReorder.reorder(submissionId, originalOrderIds);
          reloadDocuments();
        reloadAllDocs();
        },
      });
    },
    [undoStack, reloadAllDocs, reloadDocuments, submissionId, selectedSection],
  );

  // phase-compiler-chrome-batch item 4 — placement ▲▼ INLINE in the CTD
  // tree. Same flow as the Workspace placement cards: the shared
  // `swapAdjacent` computes the new order, the same reorder endpoint commits
  // it, and the same undo-stack push makes it reversible. The cards keep
  // their controls; this adds the tree surface, driven by the same per-doc
  // lock state as the padlocks (locked leaves render no arrows at all —
  // SectionTree owns that rule).
  const handleTreeMoveDocument = useCallback(
    (docId: string, direction: 'up' | 'down'): void => {
      if (!submissionId || !canEditDocuments) return;
      const doc = allDocsForTree.find((d) => d.Id === docId);
      if (!doc) return;
      const key = sectionKey(doc.CtdSectionCode ?? doc.Classification ?? '');
      const bucket = key === '' ? treeDocBuckets.unplaced : (treeDocBuckets.byKey.get(key) ?? []);
      const newOrder = swapAdjacent(bucket, docId, direction);
      if (newOrder === bucket) return; // boundary / unknown id — no-op
      const originalOrderIds = bucket.map((d) => d.Id);
      void (async () => {
        try {
          await v2.documentReorder.reorder(submissionId, newOrder.map((d) => d.Id));
          reloadDocuments();
          reloadAllDocs();
          undoStack.push({
            description: `Reorder documents in ${key || 'Unplaced'}`,
            undo: async () => {
              await v2.documentReorder.reorder(submissionId, originalOrderIds);
              reloadDocuments();
              reloadAllDocs();
            },
          });
        } catch (err) {
          showToast({
            type: 'error',
            text: `Reorder failed: ${err instanceof Error ? err.message : String(err)}`,
          });
        }
      })();
    },
    [
      submissionId,
      canEditDocuments,
      allDocsForTree,
      treeDocBuckets,
      reloadDocuments,
      reloadAllDocs,
      undoStack,
      showToast,
    ],
  );

  // phase-compiler-chrome-batch item 2 — the preview header's document ◀▶
  // flips through THE SAME ordering the tree and the grouped Workspace list
  // render: buildDocGroups (Unplaced first, then tree order), flattened.
  const orderedNavDocs = useMemo(
    () => buildDocGroups(tree.data?.tree, allDocsForTree).flatMap((g) => g.docs),
    [tree.data, allDocsForTree],
  );
  const navIdx = useMemo(
    () =>
      selectedDocumentId ? orderedNavDocs.findIndex((d) => d.Id === selectedDocumentId) : -1,
    [orderedNavDocs, selectedDocumentId],
  );

  // phase-compiler-tab-pins (layout-followup Item A) — the navigation-review-
  // capable documents, in the SAME canonical order (buildDocGroups) that the
  // tree and the document ◀▶ use. Applicability is "navigation-review-capable
  // PDF" via the ONE canonical predicate (navigationApplicability.ts): a
  // protected / read-only PDF is still inspectable, so this is NOT "has
  // bookmarks" and NOT "can edit". Bookmarks & Links walks ONLY these, so its
  // Previous/Next skip unsupported formats by construction.
  const navApplicableDocs = useMemo(
    () => navigationApplicableDocs(orderedNavDocs, navCapability.map),
    [orderedNavDocs, navCapability.map],
  );
  const navAppIdx = useMemo(
    () =>
      selectedDocumentId ? navApplicableDocs.findIndex((d) => d.Id === selectedDocumentId) : -1,
    [navApplicableDocs, selectedDocumentId],
  );
  const workspaceDraftCount = workspace?.draftSources?.size ?? 0;
  // Auto-select the FIRST applicable document when Bookmarks & Links becomes visible
  // with no applicable document selected (layout-followup Item A), hardened per Kimi
  // D3:
  //   • It fires at most ONCE per visible session (the ref), so a deliberate choice
  //     made WHILE the tab is open — including an INELIGIBLE document or a section
  //     click that clears the selection — is never overridden, and the discard-then-
  //     reselect loop cannot happen.
  //   • It is SUPPRESSED while any workspace draft is live (a rename / lifecycle /
  //     navigation draft on the current document): the auto-switch must honour the
  //     same workspace-wide guard as an explicit switch, so it never yanks the
  //     context out from under unsaved work. When the draft clears this effect
  //     re-runs and selects then (still prompt-free: no draft remains).
  //   • Direct (unguarded) set is correct ONCE those gates pass: there is no draft
  //     and no applicable document selected, so there is nothing to prompt about.
  // Late async arrival of the capability map / document list is handled by the deps:
  // until an applicable document exists the ref stays unset, so the first one that
  // arrives is still auto-selected (unless the user has meanwhile taken control).
  useEffect(() => {
    if (!bookmarksVisible) {
      bookmarksAutoSelectDoneRef.current = false;
      return;
    }
    if (bookmarksAutoSelectDoneRef.current) return;
    // Wait for a FRESH capability generation before deciding ANYTHING — this is
    // checked BEFORE the navAppIdx branch so a re-entry / reload cannot mark the
    // session "done" (or select) from the prior generation's map (Kimi G1). `stale`
    // is derived synchronously, so it is already true on the invalidation commit.
    if (navCapability.stale) return;
    if (navAppIdx >= 0) {
      bookmarksAutoSelectDoneRef.current = true;
      return;
    }
    if (workspaceDraftCount > 0) return;
    const first = navApplicableDocs[0];
    if (!first) return;
    bookmarksAutoSelectDoneRef.current = true;
    selectDocumentUnguarded(first.Id);
  }, [bookmarksVisible, navAppIdx, navApplicableDocs, workspaceDraftCount, navCapability.stale, selectDocumentUnguarded]);

  // Phase compiler-undo-stack (TODO-057) — clear the stack on section change
  // to avoid cross-section undo confusion.
  useEffect(() => {
    undoStack.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection]);

  // -- Empty / no-submission state -------------------------------------------
  if (!submissionId) {
    return (
      <section data-testid="compiler-page" style={wrapperStyle}>
        {draftDialog}
        <header>
          {/* phase-ui-compiler-header-breadcrumb-parity — the bare /compiler
              route has no submission to link back to, so its breadcrumb goes
              to the Dashboard (same ghost style as everywhere else). */}
          <div className="row-start">
            <Link to="/home" className="btn btn-ghost" data-testid="compiler-breadcrumb">
              ← Dashboard
            </Link>
            {/* phase-v1-ui-parity — V1 ReviewScreen titles this "Submission
                Compilation" with the V1 subtitle, via .screen-title/.screen-subtitle. */}
            {!submission.data && <h1 className="screen-title" style={{ margin: 0 }}>Submission Compilation</h1>}
          </div>
          <div className="screen-subtitle" style={{ marginBottom: 0 }}>
            Build and review your eCTD submission package
          </div>
        </header>
        <div data-testid="compiler-no-submission" style={pickSubmissionStyle}>
          No submission selected. Pick one from the{' '}
          <Link to="/home" style={{ color: 'var(--accent)' }}>
            Dashboard
          </Link>{' '}
          and use the per-submission Compiler link, or deep-link to{' '}
          <code>/submissions/&lt;id&gt;/compiler</code>.
        </div>
      </section>
    );
  }

  // -- Compose the six workspace panels --------------------------------------
  // phase-compiler-six-tabs — the right-pane DocumentInspector and the middle-
  // pane tab strip are both gone; their surfaces are redistributed across the
  // six always-present Workspace tabs below. Each panel is a plain ReactNode (or
  // a render-prop for Bookmarks & Links) handed to CompilerWorkspace, which keeps
  // every visited panel mounted so drafts survive a tab switch.
  const sectionOptions: readonly SectionOption[] = flattenTree(tree.data?.tree ?? []);
  const compareSiblings = allDocsForTree.map((d) => ({ id: d.Id, fileName: d.FileName }));
  const sequenceLocked = submission.data ? isSequenceLocked(submission.data) : true;
  const headerCanEdit = canEditDocuments && selectedDocument?.IsLocked !== true;
  const detailCanApprove =
    canSign && submission.data !== null && !sequenceLocked && selectedDocument?.IsLocked !== true;

  // The ONE shared selected-file header, rendered once by the shell above every
  // tab. Its document picker keeps the selection reachable when the CTD tree is
  // hidden or the screen is narrow (prompt §20). Selecting there is a plain
  // selection — handleSelectDocument never forces a main-tab switch.
  const selectedFileHeader = (
    <SelectedFileHeader
      documents={allDocsForTree}
      selectedDocument={selectedDocument}
      canEdit={headerCanEdit}
      onSelectDocument={(docId) => {
        void handleSelectDocument(docId);
      }}
      onRenamed={(newName) => {
        showToast({ type: 'success', text: `Filename updated to "${newName}".` });
        reloadDocuments();
        reloadAllDocs();
      }}
      onError={(message) => showToast({ type: 'error', text: message })}
      onSnapshotCaptured={() => {
        showToast({ type: 'success', text: 'Cover letter snapshot updated; banner cleared.' });
        reloadDocuments();
        reloadAllDocs();
      }}
    />
  );

  const documentsListBody =
    selectedSection !== null ? (
      <>
        {/* phase-ui-compiler-tree-documents-pane — return to the grouped all-
            documents view. */}
        <button
          type="button"
          data-testid="show-all-sections-btn"
          onClick={() => {
            void handleSelectSection(null);
          }}
          style={{
            background: 'none',
            border: '1px solid var(--border)',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 11,
            padding: '3px 9px',
            color: 'var(--accent)',
            marginBottom: 6,
          }}
        >
          ⊞ Show all sections
        </button>
        <DocumentList
          sectionCode={selectedSection}
          documents={documents.data ?? []}
          selectedDocumentId={selectedDocumentId}
          submissionId={submissionId ?? null}
          canEdit={canEditDocuments}
          sequenceLocked={sequenceLocked}
          region={submission.data?.Region ?? undefined}
          onSelect={handleSelectDocument}
          loading={documents.loading}
          error={documents.error}
          onListChanged={reloadDocuments}
          onError={(text) => showToast({ type: 'error', text })}
          onBatchConfirmed={handleBatchConfirmed}
          onBatchLocked={handleBatchLocked}
          onLockToggled={handleLockToggled}
          onReordered={handleReordered}
        />
      </>
    ) : allDocsForTree.length === 0 ? (
      <div
        data-testid="compiler-empty-sequence"
        style={{ padding: '18px 6px', color: 'var(--text-muted)', fontSize: 13 }}
      >
        No documents in this sequence yet. Upload documents from the Submission Details page to
        start assembling.
      </div>
    ) : (
      <div data-testid="all-documents-grouped">
        <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 6px 0' }}>
          All documents ({allDocsForTree.length}) grouped by section — click a section in the tree
          to focus.
        </div>
        {buildDocGroups(tree.data?.tree, allDocsForTree).map((g) => (
          <div key={g.code ?? '__unplaced'} data-testid={`doc-group-${g.code ?? 'unplaced'}`}>
            <div style={groupHeaderStyle}>
              {g.code === null ? '⚠ ' : ''}
              {g.label} ({g.docs.length})
            </div>
            <DocumentList
              sectionCode={g.code}
              hideHeader
              documents={g.docs}
              selectedDocumentId={selectedDocumentId}
              submissionId={submissionId ?? null}
              canEdit={canEditDocuments}
              sequenceLocked={sequenceLocked}
              region={submission.data?.Region ?? undefined}
              onSelect={handleSelectDocument}
              loading={false}
              error={null}
              onListChanged={reloadAllDocs}
              onError={(text) => showToast({ type: 'error', text })}
              onBatchConfirmed={handleBatchConfirmed}
              onBatchLocked={handleBatchLocked}
              onLockToggled={handleLockToggled}
              onReordered={handleReordered}
            />
          </div>
        ))}
      </div>
    );

  const documentsPanel = (
    <div data-testid="workspace-documents">
      <div
        data-testid="submission-document-counts"
        style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 12 }}
      >
        Submission: {allDocsForTree.length} document{allDocsForTree.length === 1 ? '' : 's'} ·{' '}
        {allDocsForTree.filter((d) => Boolean(d.ConfirmedAt)).length} confirmed
      </div>
      <CompilerToolbar
        submissionId={submissionId}
        sequenceNumber={submission.data?.SequenceNumber ?? null}
        onBadgeClick={openValidationSubmission}
        totalCount={documents.data?.length ?? 0}
        reviewedCount={
          documents.data?.filter(
            (d) => typeof d.ConfirmedAt === 'string' && d.ConfirmedAt.length > 0,
          ).length ?? 0
        }
        reclassifiedCount={
          documents.data?.filter((d) => {
            const approved = d['ClassificationApprovedValue'] as string | null | undefined;
            return (
              typeof approved === 'string' &&
              approved.length > 0 &&
              approved !== (d.Classification ?? null)
            );
          }).length ?? 0
        }
        continuousValidationOn={continuousValidationOn}
        canUndo={undoStack.canUndo}
        undoLabel={undoStack.undoLabel}
        onUndo={() => {
          if (undoStack.canUndo) {
            const entry = undoStack.pop();
            if (entry) {
              entry.undo().catch((err: unknown) => {
                undoStack.push(entry);
                showToast({
                  type: 'error',
                  text: `Undo failed: ${err instanceof Error ? err.message : String(err)}`,
                });
              });
            }
          }
        }}
      />

      {/* Concise view selector — list vs the submission-wide classification
          worklist. NOT a nested tablist (buttons + aria-pressed). */}
      <div
        role="group"
        aria-label="Documents view"
        style={{ display: 'inline-flex', gap: 2, margin: '8px 0', border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}
      >
        <button
          type="button"
          data-testid="documents-view-list"
          aria-pressed={docsView === 'list'}
          onClick={() => setDocsView('list')}
          style={{
            padding: '4px 12px',
            fontSize: 12,
            fontWeight: docsView === 'list' ? 700 : 500,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            background: docsView === 'list' ? 'var(--accent, #4f46e5)' : 'transparent',
            color: docsView === 'list' ? '#fff' : 'var(--text-muted)',
          }}
        >
          Documents
        </button>
        <button
          type="button"
          data-testid="documents-view-classify"
          aria-pressed={docsView === 'classify'}
          onClick={() => setDocsView('classify')}
          style={{
            padding: '4px 12px',
            fontSize: 12,
            fontWeight: docsView === 'classify' ? 700 : 500,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            background: docsView === 'classify' ? 'var(--accent, #4f46e5)' : 'transparent',
            color: docsView === 'classify' ? '#fff' : 'var(--text-muted)',
          }}
        >
          Classification worklist
        </button>
      </div>

      {docsView === 'list' ? (
        documentsListBody
      ) : (
        <ClassificationTabPanel
          documents={allDocsForTree}
          canClassify={canEditDocuments}
          onClassifyAll={handleClassifyAll}
          onReclassify={handleReclassifyDocument}
        />
      )}

      {selectedDocument && (
        <DocumentDetailSections
          document={selectedDocument}
          sections={sectionOptions}
          canEdit={headerCanEdit}
          canApprove={detailCanApprove}
          onDocumentUpdated={() => {
            reloadDocuments();
            reloadAllDocs();
          }}
          onToast={showToast}
        />
      )}
    </div>
  );

  const reviewPanel = (
    <WorkspaceReviewPanel
      document={selectedDocument}
      submissionId={submissionId}
      siblings={compareSiblings}
      page={viewerPage}
      onPageChange={setViewerPage}
      hasPrevDocument={navIdx > 0}
      hasNextDocument={navIdx >= 0 && navIdx < orderedNavDocs.length - 1}
      onPrevDocument={() => {
        const prev = orderedNavDocs[navIdx - 1];
        if (prev) void handleSelectDocument(prev.Id);
      }}
      onNextDocument={() => {
        const next = orderedNavDocs[navIdx + 1];
        if (next) void handleSelectDocument(next.Id);
      }}
    />
  );

  // Bookmarks & Links is a render-prop so it learns whether it is the active tab
  // (its navigation-envelope fetch refreshes on re-entry; its expanded overlay
  // folds when hidden) WITHOUT being keyed on the tab — keying would remount it
  // and drop a staged reason. The snapshot control sits above the editor and,
  // on a successful apply, bumps navRefreshNonce so the editor re-reads the
  // envelope and unlocks in place.
  const renderBookmarksLinks = (active: boolean): ReactNode => {
    // Honest capability states (Kimi D2 follow-up): while the authoritative map is
    // loading for the FIRST time, or its fetch errored with no prior map, we must NOT
    // render a false "no reviewable documents" success — show loading / an error with
    // retry. A background REFRESH (map still present) keeps the existing content and
    // mounted editor/draft; only the auto-select + Previous/Next are disabled via the
    // `stale` gate, so a routine refresh never tears down unsaved work.
    const hasReviewableSelection = selectedDocument !== null && navAppIdx >= 0;
    if (navCapability.loading || (navCapability.refreshing && !hasReviewableSelection)) {
      return (
        <WorkspaceEmptyState testId="workspace-bookmarks-loading">
          Checking which documents can be reviewed for bookmarks and hyperlinks…
        </WorkspaceEmptyState>
      );
    }
    if (navCapability.error && !hasReviewableSelection) {
      return (
        <div data-testid="workspace-bookmarks-error" style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
          <p style={{ margin: '0 0 8px' }}>
            Couldn’t load which documents are navigation-reviewable: {navCapability.error}
          </p>
          <button
            type="button"
            data-testid="workspace-bookmarks-retry"
            onClick={() => void navCapability.refetch()}
            style={bookmarksNavButtonStyle}
          >
            Retry
          </button>
        </div>
      );
    }
    // No navigation-review-capable document in the whole submission — an honest
    // empty state rather than an editor pointed at an unsupported file.
    if (navApplicableDocs.length === 0) {
      return (
        <WorkspaceEmptyState testId="workspace-bookmarks-none-applicable">
          No navigation-reviewable documents yet. Bookmarks and hyperlinks are reviewed on
          PDF artifacts — a Word document is reviewable here only where it ships as PDF; a
          Word file that ships as Word (e.g. a Health Canada / EU Word slot) is covered by
          the Word link checks, not this editor.
        </WorkspaceEmptyState>
      );
    }
    // The selected document is the applicable one the auto-select settled on (or
    // the operator picked). A brief frame may pass before the effect replaces a
    // non-applicable selection; until then show the empty prompt rather than an
    // editor on an unsupported file.
    if (!selectedDocument || navAppIdx < 0) {
      return (
        <WorkspaceEmptyState testId="workspace-bookmarks-empty">
          Select a navigation-reviewable document to edit its bookmarks and hyperlinks. The
          submission navigation policy is applied per document.
        </WorkspaceEmptyState>
      );
    }
    return (
      <div data-testid="workspace-bookmarks-links">
        {/* Inline capability status (Kimi G2): a refresh / recoverable error with a
            PRIOR map keeps the SAME mounted editor + its stateful children below, so
            unsaved navigation drafts are preserved; we only surface the status + a
            retry and disable Previous/Next (via the `stale` gate). */}
        {navCapability.error && navCapability.map ? (
          <div
            data-testid="workspace-bookmarks-refresh-error"
            role="status"
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}
          >
            <span>Couldn’t refresh which documents are reviewable: {navCapability.error}</span>
            <button
              type="button"
              data-testid="workspace-bookmarks-refresh-retry"
              onClick={() => void navCapability.refetch()}
              style={bookmarksNavButtonStyle}
            >
              Retry
            </button>
          </div>
        ) : navCapability.refreshing ? (
          <div
            data-testid="workspace-bookmarks-refreshing"
            role="status"
            style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}
          >
            Refreshing which documents are reviewable…
          </div>
        ) : null}
        <div
          data-testid="bookmarks-doc-nav"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            data-testid="bookmarks-prev-doc"
            aria-label="Previous document"
            disabled={navAppIdx <= 0 || navCapability.stale}
            onClick={() => {
              const prev = navApplicableDocs[navAppIdx - 1];
              if (prev) void handleSelectDocument(prev.Id);
            }}
            style={bookmarksNavButtonStyle}
          >
            ◀ Previous
          </button>
          <span data-testid="bookmarks-doc-index" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Document {navAppIdx + 1} of {navApplicableDocs.length}
          </span>
          <button
            type="button"
            data-testid="bookmarks-next-doc"
            aria-label="Next document"
            disabled={navAppIdx >= navApplicableDocs.length - 1 || navCapability.stale}
            onClick={() => {
              const next = navApplicableDocs[navAppIdx + 1];
              if (next) void handleSelectDocument(next.Id);
            }}
            style={bookmarksNavButtonStyle}
          >
            Next ▶
          </button>
        </div>
        <NavigationSnapshotControls
          submissionId={submissionId}
          onCaptured={() => {
            setNavRefreshNonce((n) => n + 1);
            reloadDocuments();
            reloadAllDocs();
          }}
        />
        <div style={{ marginTop: 12 }}>
          <NavigationTab
            documentId={selectedDocument.Id}
            fileName={(selectedDocument.FileName as string | null | undefined) ?? null}
            operation={selectedDocument.LifecycleOperation as string | null | undefined}
            canEdit={headerCanEdit}
            active={active}
            onShowLinks={handleOpenSelectedDocumentLinks}
            refreshNonce={navRefreshNonce}
            embedded
          />
        </div>
      </div>
    );
  };

  const validationPanel = (
    <div data-testid="workspace-validation">
      {/* Explicit scope selector — submission-wide vs the selected document. */}
      <div
        role="group"
        aria-label="Validation scope"
        style={{ display: 'inline-flex', gap: 2, marginBottom: 12, border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}
      >
        <button
          type="button"
          data-testid="validation-scope-submission"
          aria-pressed={validationView === 'submission'}
          onClick={() => setValidationView('submission')}
          style={{
            padding: '4px 12px',
            fontSize: 12,
            fontWeight: validationView === 'submission' ? 700 : 500,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            background: validationView === 'submission' ? 'var(--accent, #4f46e5)' : 'transparent',
            color: validationView === 'submission' ? '#fff' : 'var(--text-muted)',
          }}
        >
          Submission
        </button>
        <button
          type="button"
          data-testid="validation-scope-document"
          aria-pressed={validationView === 'document'}
          onClick={() => setValidationView('document')}
          style={{
            padding: '4px 12px',
            fontSize: 12,
            fontWeight: validationView === 'document' ? 700 : 500,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            background: validationView === 'document' ? 'var(--accent, #4f46e5)' : 'transparent',
            color: validationView === 'document' ? '#fff' : 'var(--text-muted)',
          }}
        >
          Selected document
        </button>
      </div>

      {validationView === 'submission' ? (
        <div data-testid="validation-submission">
          {/* The worklist, embedded (no bottom drawer) — honest empty states. */}
          <CompilerNotificationDrawer
            embedded
            documents={allDocsForTree}
            validation={validation.data}
            onNavigateToDocument={handleNavigateToDocument}
            onOpenValidationReport={() => setValidationReportOpen(true)}
          />
          {/* Submission-wide link inventory; a row drills into the source
              document on the Selected-document scope. */}
          {submissionId ? (
            <div style={{ marginTop: 16 }}>
              <SubmissionLinksPanel
                submissionId={submissionId}
                onOpenDocumentLinks={handleOpenDocumentLinks}
              />
            </div>
          ) : null}
        </div>
      ) : selectedDocument ? (
        <div data-testid="validation-document">
          <NavigationValidationPanel
            documentId={selectedDocument.Id}
            submissionId={submissionId}
            canRevalidate={headerCanEdit}
            onRevalidated={() => {
              void fetchValidation();
            }}
          />
          <div style={{ marginTop: 16 }}>
            <NavigationReleaseReview submissionId={submissionId} documentId={selectedDocument.Id} />
          </div>
        </div>
      ) : (
        <WorkspaceEmptyState testId="validation-document-empty">
          Select a document to see its link validation, regional navigation criteria and release
          sign-off.
        </WorkspaceEmptyState>
      )}
    </div>
  );

  // phase-compiler-tab-pins — the Lifecycle tab leads with an honest overview
  // (current operation + recorded history, always visible) and tucks the editor
  // behind a collapsed native disclosure. Keying by the document id resets the
  // disclosure to collapsed only on a genuine document change; an ordinary tab
  // switch keeps the panel mounted so any staged draft survives.
  const lifecyclePanel = selectedDocument ? (
    <LifecycleWorkspacePanel
      key={selectedDocument.Id}
      submissionId={submissionId}
      document={selectedDocument}
      canEdit={headerCanEdit}
      onSuccess={(op) => {
        showToast({ type: 'success', text: `${String(op).toUpperCase()} operation and prior target saved.` });
        reloadDocuments();
        reloadAllDocs();
      }}
      onError={(message) => showToast({ type: 'error', text: message })}
    />
  ) : (
    <WorkspaceEmptyState testId="workspace-lifecycle-empty">
      Select a document to set its lifecycle operation (new / replace / append / delete) and review
      its history.
    </WorkspaceEmptyState>
  );

  const packagePanel = (
    <div data-testid="workspace-package" style={{ padding: '8px 0' }}>
      {submissionId ? <PackageViewer submissionId={submissionId} /> : null}
      {selectedDocument && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <PackageTab
            fileName={(selectedDocument.FileName as string | null | undefined) ?? null}
            ctdSectionCode={(selectedDocument.CtdSectionCode as string | null | undefined) ?? null}
            moduleNumber={(selectedDocument.ModuleNumber as string | null | undefined) ?? null}
          />
        </div>
      )}
    </div>
  );

  const submissionLike: SubmissionLike | null = submission.data
    ? {
        DrugName: submission.data.DrugName,
        Region: submission.data.Region as Region,
        Status: submission.data.Status ?? null,
        SequenceNumber: submission.data.SequenceNumber ?? null,
        NdaNumber: submission.data.NdaNumber ?? null,
        SubmissionType: submission.data.SubmissionType ?? null,
        UpdatedAt: submission.data.UpdatedAt,
      }
    : null;

  // phase-ui-compiler-header-breadcrumb-parity — same header data Details
  // computes, so both pages render an identical hero for the same submission.
  // Type label mirrors SubmissionDetailPage's formattedTypeLabel; counts come
  // from allDocsForTree (already loaded for the section-tree badges) using the
  // same predicates as Details' gate.
  const compilerTypeLabel = ((): string | null => {
    const appType = submission.data?.SubmissionType ?? null;
    const typeCode = submission.data?.SubmissionTypeCode ?? null;
    const subTypeCode = submission.data?.SubmissionSubTypeCode ?? null;
    if (!appType) return null;
    if (!typeCode) return appType;
    if (subTypeCode) return `${appType} — ${typeCode} / ${subTypeCode}`;
    return `${appType} — ${typeCode}`;
  })();
  return (
    <section data-testid="compiler-page" style={wrapperStyle}>
      {draftDialog}
      {submission.data && <SubmissionWorkspaceTabs submissionId={submissionId} name={submission.data.DrugName} sequence={submission.data.SequenceNumber} active="compilation" header={<SubmissionIdentityHeader submission={submission.data} statusEvidence={statusEvidence.get(submissionId ?? '')} submissionTypeLabel={compilerTypeLabel ?? undefined} showSequence onRelinkProfileClick={() => { setEditingMetadata(true); }} headerActions={<>
                {/* phase-fix-submission-status-truth (§3.4) — a locked sequence
                hides its Edit affordance (server rejects the edit too). */}
                {canWriteSubmission && (!isSequenceLocked(submission.data) ||
                  submission.data.ActiveUnlocks?.some((grant) => grant.Kind === 'edit')) ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="compiler-edit-submission-btn"
                    title="Edit submission metadata"
                    aria-label="Edit submission metadata"
                    onClick={() => { setEditingMetadata(true); }}
                  >
                    ✎ Edit
                  </Button>
                ) : canWriteSubmission && isSequenceLocked(submission.data) ? (
                  <span
                    data-testid="compiler-edit-submission-locked"
                    title={LOCKED_EDIT_TOOLTIP}
                    aria-label={LOCKED_EDIT_TOOLTIP}
                    style={{
                      // phase-ui-hero-right-metadata-contrast — explicit
                      // light text on the navy band (was inherited-dark at
                      // 0.6 opacity: invisible).
                      color: 'rgba(255,255,255,0.75)',
                      fontSize: '0.85rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    🔒 Locked
                  </span>
                ) : undefined}
                <SubmissionDeleteButton submissionId={submissionId} name={submission.data.DrugName} sequence={submission.data.SequenceNumber} />
              </>} />} />}
      {submissionId && submission.data && <CompilerSpecificationPanel submissionId={submissionId} region={submission.data.Region} ectdVersion={submission.data.EctdVersion} onPublished={() => { void reloadSubmission(); reloadDocuments(); reloadAllDocs(); }} />}
      <header>
        {!submission.data && (
          <SubmissionWorkspaceLoading submissionId={submissionId} active="compilation" failed={Boolean(submission.error)} />
        )}
        {submission.loading ? (submission.data ? (
          <p data-testid="compiler-submission-loading" style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
            Loading submission…
          </p>
        ) : null) : submission.error ? (
          <p
            data-testid="compiler-submission-error"
            role="alert"
            style={{ margin: '4px 0 0 0', color: 'var(--danger, #b91c1c)' }}
          >
            Could not load submission: {submission.error}
          </p>
        ) : submissionLike && submission.data ? (
          <div style={{ marginTop: 8 }}>
            {editingMetadata && submission.data && (
              <EditSubmissionModal
                submission={submission.data}
                onClose={() => setEditingMetadata(false)}
                onSaved={() => {
                  void reloadSubmission();
                }}
              />
            )}
            {/* Shared progress indicators, also shown on Submission Detail.
                Collapsible on operator request — hidden when the process-status
                strip is closed to free vertical space for the workspace. */}
            {processStatusOpen && (
              <EvidenceStatusPipeline submissionId={submissionId ?? ""} evidence={statusEvidence.get(submissionId ?? "")} onRefresh={statusEvidence.refresh} />
            )}
          </div>
        ) : null}
      </header>

      {/* User-requested hide control for the process-status + action strip.
          Hiding collapses the status pipeline (above) AND the banner/compile/
          report strip (below) to reclaim vertical space; a compact Validation
          link stays so the worklist is still reachable in one click. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0' }}>
        <button
          type="button"
          data-testid="compiler-process-status-toggle"
          aria-expanded={processStatusOpen}
          onClick={() => setProcessStatusOpen(!processStatusOpen)}
          style={{
            padding: '3px 10px',
            fontSize: 12,
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 6,
            cursor: 'pointer',
            color: 'var(--text, inherit)',
          }}
        >
          {processStatusOpen ? 'Hide process status' : 'Show process status'}
        </button>
        {!processStatusOpen && (
          <button
            type="button"
            data-testid="compiler-validation-link"
            onClick={openValidationSubmission}
            style={{
              padding: 0,
              fontSize: 12,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--accent, #4f46e5)',
              textDecoration: 'underline',
            }}
          >
            Open validation
          </button>
        )}
      </div>

      {/* Wave E.4 — submission-wide validation banner sits above the workspace.
          Shows aggregate status + a "Validate all" trigger. The toolbar
          ValidationScoreBadge now opens the Validation tab directly
          (openValidationSubmission), so this strip no longer needs a scroll
          target. Collapsed with the process-status strip on operator request. */}
      <div
        className="compiler-action-strip"
        hidden={!processStatusOpen}
        style={processStatusOpen ? undefined : { display: 'none' }}
      >
        {submission.data?.EctdVersion === '4.0' && <p role="note" style={{ fontSize: 13, lineHeight: 1.5, margin: '8px 0' }}><strong>Source document checks</strong> — these results cover the working documents. Generated package checks and final publication readiness are shown with each candidate above.</p>}
        <ValidationStatusBanner
          showValidateAction
          data={validation.data}
          loading={validation.loading}
          error={validation.error}
          validating={validatingNow}
          canValidate={canWrite}
          onValidateAll={() => {
            void handleValidateAll();
          }}
          onOpenWorklist={openValidationSubmission}
        />
        {/* Phase UI-13 — View Report button next to the banner. */}
        {validation.data ? (
          <div className="compiler-report-actions">
            <button
              type="button"
              data-testid="view-report-btn"
              onClick={() => setValidationReportOpen(true)}
              style={{
                padding: '4px 12px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              View report
            </button>
            {/* Phase UI-13 — Unlock button next to the banner toolbar. */}
            <UnlockSequenceButton
              submissionId={submissionId}
              canUnlock={hasPermission('Submissions', 'unlock')}
              currentStatus={submission.data?.Status ?? null}
              onUnlocked={() => {
                void reloadSubmission();
              }}
            />
          </div>
        ) : null}
        {!publishMode && <>
      {!permissionsLoading && !canWriteSubmission && (
        <div
          data-testid="compile-permission-note"
          role="note"
          style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}
        >
          Compiling needs the Submissions:write permission.
        </div>
      )}
      {canWriteSubmission && submission.data?.EctdVersion !== '4.0' && (
      <CompileSubmissionPanel
        submissionId={submissionId ?? null}
        canCompile={canWriteSubmission}
        validation={validation.data}
        submissionSequenceNumber={submission.data?.SequenceNumber ?? null}
        onPublishStart={() => setPublishMode(true)}
        companyProfileLinkStatus={submission.data?.CompanyProfileLinkStatus}
      />
      )}

        </>}
      </div>

      {/* Phase UI-13 — Modals (mounted near top so they overlay everything). */}
      <ValidationReportModal
        isOpen={validationReportOpen}
        data={validation.data}
        loading={validation.loading}
        error={validation.error}
        onRetry={() => { void fetchValidation(); }}
        onClose={() => setValidationReportOpen(false)}
      />
      <AgencyConfirmModal
        isOpen={agencyConfirmOpen}
        busy={agencyConfirmBusy}
        onYes={() => {
          void handleAgencyConfirm();
        }}
        onLater={() => setAgencyConfirmOpen(false)}
      />
      <SequenceWarningModal
        isOpen={priorWarning !== null}
        priorSequenceNumber={priorWarning?.sequenceNumber ?? ''}
        priorStatus={priorWarning?.status ?? ''}
        onProceed={() => setPriorWarning(null)}
        onCancel={() => setPriorWarning(null)}
      />

      {/* TODO-081 — cross-section drag reclassification confirm dialog */}
      {reclassifyTarget !== null && (
        <ReclassifyConfirmDialog
          documentName={reclassifyTarget.documentName}
          fromSection={reclassifyTarget.fromSection}
          toSection={reclassifyTarget.toSection}
          onConfirm={() => { void handleReclassifyConfirm(); }}
          onCancel={() => setReclassifyTarget(null)}
        />
      )}
      <PageToast toast={toast} onDismiss={clearToast} />

      {/* Phase phase-publish-as-mode (TODO-022) — when publishMode is on,
          the assembly UI below is replaced by the PublishWizard. The
          Compile button in CompileSubmissionPanel flips publishMode via
          the onPublishStart callback. The wizard's own Cancel / Done
          exits it. */}
      {publishMode && submissionId ? (
        <PublishWizard
          submissionId={submissionId}
          submission={submission.data}
          validation={validation.data}
          onCancel={() => setPublishMode(false)}
          onComplete={() => {
            setPublishMode(false);
            void reloadSubmission();
            void handleValidateAll();
          }}
        />
      ) : (
        <>
      {/* phase-compiler-tab-pins — the pinnable-tab workspace shell replaces the
          old 3-pane cards + middle-tab strip + right-pane Inspector. The CTD tree
          is now a first-class pinnable tab supplied here; the six task panels +
          the one shared selected-file header are built above. A main-tab switch
          never calls mayChangeContext — every visited panel stays mounted, so
          drafts survive. */}

      <CompilerWorkspace
        active={workspaceTab}
        onSelect={setWorkspaceTab}
        header={selectedFileHeader}
        pinned={pinnedTabs}
        onTogglePin={togglePin}
        treeRatio={treeRatio}
        onTreeRatioChange={setTreeRatio}
        viewPreset={viewPreset}
        onSelectPreset={selectPreset}
        documentsPanel={documentsPanel}
        reviewPanel={reviewPanel}
        renderBookmarksLinks={renderBookmarksLinks}
        validationPanel={validationPanel}
        lifecyclePanel={lifecyclePanel}
        packagePanel={packagePanel}
        treePanel={
          <div
            style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
            onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
            onDrop={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
          >
            <ApplicationSectionTree
              submissionId={submissionId}
              sequenceNumber={submission.data?.SequenceNumber ?? null}
              sequenceDescription={submission.data?.SequenceDescription ?? null}
              documents={allDocsForTree}
              documentsLoading={allDocsLoading}
              documentsError={allDocsError}
              onRetryDocuments={reloadAllDocs}
              onOpenSequenceDocument={async (ownerId, documentId) => {
                if (workspace?.draftSources?.size && !(await mayChangeContext())) return;
                navigate(`/submissions/${encodeURIComponent(ownerId)}/compiler?documentId=${encodeURIComponent(documentId)}`);
              }}
              tree={tree.data?.tree ?? []}
              selectedCode={selectedSection}
              onSelect={handleSelectSection}
              loading={tree.loading}
              error={tree.error}
              subtitle={tree.data?.dtdVersion}
              region={tree.data?.region}
              {...(canWriteTree ? { onDropDocument: handleDropDocument } : {})}
              selectedDocumentId={selectedDocumentId}
              onSelectDocument={handleSelectDocument}
              priorSeqByDocId={priorSeqByDocId}
              storageKey={submissionId ? `compiler-tree-expanded-${submissionId}` : undefined}
              {...(canWriteTree ? { onMoveDocument: handleTreeMoveDocument } : {})}
              {...(canWriteTree ? { onDropFiles: handleDropFiles } : {})}
            />
          </div>
        }
      />
        </>
      )}
    </section>
  );
}

/**
 * Route entry point. Reads the `:submissionId` param and KEYS the inner screen
 * by it so switching submissions (or dropping to the bare `/compiler` route)
 * remounts a fresh instance — the only safe reset for the hydrate-once
 * per-submission view state, so one submission's section/document selection and
 * in-flight forms can never bleed into another.
 */
export function CompilerPage(): ReactElement {
  const { submissionId } = useParams<{ submissionId?: string }>();
  return <CompilerPageInner key={submissionId ?? '__no-submission__'} submissionId={submissionId} />;
}
